import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, ExternalLink, PawPrint } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { chatbotConfig, siteConfig } from '../data/mock';
import { trackWhatsAppClick } from '../lib/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 1,
        role: 'assistant',
        content: chatbotConfig.welcomeMessage,
        timestamp: new Date()
      }]);
    }
  }, [messages.length]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const sendMessage = async (messageText = null) => {
    // Ensure we always work with a plain string
    let text = '';
    
    if (messageText !== null && messageText !== undefined) {
      // If messageText is provided, convert to string safely
      text = typeof messageText === 'string' ? messageText.trim() : String(messageText).trim();
    } else {
      // Use input value
      text = inputValue.trim();
    }
    
    // Validate that we have actual text content
    if (!text || typeof text !== 'string' || text.length === 0 || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Check for "visitas" keyword locally first
    const lowerText = text.toLowerCase();
    if (lowerText.includes('visita') || lowerText.includes('domicilio')) {
      const visitasResponse = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Actualmente no ofrecemos visitas a domicilio.\nPuedo ayudarte con guardería de día, guardería de noche, alojamiento o paseos diarios 😊',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, visitasResponse]);
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        message: String(text), // Ensure string is sent to API
        session_id: sessionId
      });

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response || 'Lo siento, no pude procesar tu mensaje.',
        whatsappLink: response.data.whatsapp_link || null,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo o contacta directamente por WhatsApp.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickButton = (service) => {
    if (typeof service === 'string' && service.length > 0) {
      sendMessage(`Necesito ${service}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button - Paw Icon */}
      <motion.button
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-all"
        style={{ 
          backgroundColor: isOpen ? '#232323' : '#5B9A6F',
          boxShadow: '0 4px 20px rgba(91, 154, 111, 0.4)'
        }}
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Abrir chat"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-7 h-7 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <PawPrint className="w-8 h-8 text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div className="p-4" style={{ backgroundColor: '#5B9A6F' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <PawPrint className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{chatbotConfig.name}</h3>
                  <p className="text-xs text-white/80">Normalmente responde en segundos</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="h-[350px] p-4">
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div key={message.id}>
                    <div
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                          message.role === 'user'
                            ? 'bg-primary text-white rounded-br-md'
                            : 'bg-accent-grey-200 text-primary rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        {message.whatsappLink && (
                          <a
                            href={message.whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackWhatsAppClick('chatbot_message')}
                            data-testid="whatsapp-btn-chatbot-message"
                            className="mt-3 flex items-center gap-2 bg-green-500 text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-green-600 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Confirmar por WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                    {/* Quick buttons after welcome message */}
                    {index === 0 && message.role === 'assistant' && messages.length === 1 && (
                      <div className="flex flex-wrap gap-2 mt-3 ml-1">
                        {chatbotConfig.quickButtons?.map((btn) => (
                          <button
                            key={btn}
                            onClick={() => handleQuickButton(btn)}
                            disabled={isLoading}
                            className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
                            style={{ color: '#5B9A6F', borderColor: '#5B9A6F' }}
                          >
                            {btn}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-accent-grey-200 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-sm text-secondary">Escribiendo...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value || '')}
                  onKeyDown={handleKeyPress}
                  placeholder="Escribe tu mensaje..."
                  className="rounded-full flex-1"
                  disabled={isLoading}
                />
                <Button
                  onClick={() => sendMessage()}
                  disabled={!inputValue || !inputValue.trim() || isLoading}
                  className="btn-primary rounded-full w-12 h-10 p-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-center text-secondary mt-2">
                O contacta por{' '}
                <a
                  href={siteConfig.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick('chatbot_footer')}
                  data-testid="whatsapp-btn-chatbot-footer"
                  className="text-green-600 font-medium"
                >
                  WhatsApp
                </a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
